<template>
  <div>
    <div class="header" :class="{ 'back-action': backButton }">
      <div v-if="backButton" class="back-icon" @click="$emit('prev-step')">
        <IconArrow class="icon-arrow" />
      </div>
      <h1 class="h4">
        Log In to Your Profile
      </h1>
    </div>
    <div v-if="!hideSocials" class="social-login-buttons">
      <div class="google-btn">
        <GoogleLoginButton
          :form-type="formType"
          :keep-me-logged-in="!!keepMeLoggedIn"
          data-event="google.click"
          data-async-event
          data-direct-send
          :resources="resources"
        />
      </div>
      <div>
        <FacebookLoginButton
          :form-type="formType"
          :keep-me-logged-in="!!keepMeLoggedIn"
          data-event="facebook.click"
          data-async-event
          data-direct-send
          :resources="resources"
        />
      </div>
      <div>
        <LinkedinLoginButton
          :form-type="formType"
          :keep-me-logged-in="!!keepMeLoggedIn"
          data-event="linkedin.click"
          data-async-event
          data-direct-send
          :resources="resources"
        />
      </div>
    </div>
    <div v-if="!hideSocials" class="divider" />
    <form
      class="login-form"
      method="post"
      @submit.prevent="loginUser"
    >
      <div class="input-group">
        <input
          id="email"
          v-model="login.email"
          type="email"
          name="email"
          autocomplete="username"
          placeholder="Email"
          :class="{ 'invalid-field': emailAlert }"
          data-event="email.click"
          data-direct-send
          @input="emailAlert = ''"
        >
        <small v-if="emailAlert" class="alert">{{ emailAlert }}</small>
      </div>
      <div class="input-group">
        <input
          id="password"
          v-model="login.password"
          :type="visiblePassword ? 'text' : 'password'"
          name="password"
          autocomplete="current-password"
          placeholder="Password"
          :class="{ password: !visiblePassword, 'invalid-field': passwordAlert }"
          data-event="password.click"
          data-direct-send
          @input="passwordAlert = ''"
        >
        <Component
          :is="visiblePassword ? 'IconEye' : 'IconEyeClosed'"
          class="icon-eye"
          @click="visiblePassword = !visiblePassword"
        />
        <small v-if="passwordAlert" class="alert">{{ passwordAlert }}</small>
      </div>
      <div class="input-group links">
        <div class="vue-checkbox">
          <input
            id="keeplogin" v-model="keepMeLoggedIn" type="checkbox"
            class="custom-checkbox reverse-colors"
            data-event="rememberme.click"
            data-direct-send
          >
          <label for="keeplogin">Remember me</label>
        </div>
        <div>
          <nuxt-link to="/forgotten-password/" data-event="forgotpswd.click">
            Forgot your password?
          </nuxt-link>
        </div>
      </div>
      <button
        class="submit btn-lg btn-primary"
        data-event="login.click"
        data-async-event="login.click"
        data-direct-send
      >
        Log In
      </button>
      <p class="or-sign-up">
        Don’t have an account?
        <span v-if="resources" @click="$emit('signup')">
          Sign Up
        </span>
        <nuxt-link v-else to="/signup/">
          Sign Up
        </nuxt-link>
      </p>
    </form>
  </div>
</template>

<script>

import IconArrow from '@itbpbg/lms-components/src/assets/svg/icon-arrow-back.svg?vue-component';
import GraduatesWork from './GraduatesWork';
import FacebookLoginButton from './FacebookLoginButton';
import GoogleLoginButton from './GoogleLoginButton';
import LinkedinLoginButton from './LinkedinLoginButton';
import IconEye from '../assets/svg/eye.svg?vue-component';
import IconEyeClosed from '../assets/svg/eye-closed.svg?vue-component';

export default {
  components: {
    GraduatesWork,
    FacebookLoginButton,
    GoogleLoginButton,
    LinkedinLoginButton,
    IconEye,
    IconEyeClosed,
    IconArrow,
  },
  props: {
    type: { type: String, default: '' },
    hideSocials: Boolean,
    backButton: Boolean,
    resources: Boolean,
  },
  data() {
    return {
      visiblePassword: false,
      login: {
        email: '',
        password: '',
      },
      formType: this.type || this.$route.name,
      keepMeLoggedIn: false,
      emailAlert: '',
      passwordAlert: '',
    };
  },
  mounted() {
    if (this.$route.query.error && this.$route.query.error === 'business') {
      this.$store.commit('auth/setLoginAlert', true);
      const query = { ...this.$route.query };
      delete query.error;
      this.$router.replace({ query });
    }
    this.url = this.$route.params.url;
  },
  methods: {
    async loginUser() {
      if (!this.login.email) {
        this.emailAlert = 'Required field';
        this.$tracking.sendAsyncEvent('login.click', { status: 'fail', error: 'Please fill in email!' });
        return;
      }

      if (!this.login.password) {
        this.passwordAlert = 'Required field';
        this.$tracking.sendAsyncEvent('login.click', { status: 'fail', error: 'Please fill in password!' });
        return;
      }

      const login = await this.$store.dispatch('auth/login', {
        email: this.login.email,
        password: this.login.password,
        keepMeLoggedIn: this.keepMeLoggedIn,
        resources: this.resources,
      });

      if (login) {
        if (login.status !== 'error') {
          if (login.firstLogin) {
            this.$router.push('/forgotten-password/');
          } else if (this.resources) {
            await this.$tracking.sendAsyncEvent('login.click', { status: 'success' });
            this.$emit('continueToExamResult');
          } else {
            await this.$tracking.sendAsyncEvent('login.click', { status: 'success' });

            if (this.$store.getters['auth/user'].role === 'instructor') {
              this.$redirectAfterLogin('/');
            } else {
              this.$redirectAfterLogin('/my-learning-path/');
            }
          }
        } else if (login.status === 'error') {
          this.emailAlert = '';
          this.passwordAlert = '';

          if (login.error === 'incorrectPassword') {
            this.passwordAlert = login.message;
          } else if (login.status === 'error') {
            this.emailAlert = login.message;
          }

          this.$tracking.sendAsyncEvent('login.click', { status: 'fail', error: login.message });
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
  .form-wrap {
    h1 {
      margin-top: 0;
      margin-bottom: var(--spacing-24);
      text-align: center;
    }

    .divider {
      position: relative;
      padding: var(--spacing-36) 0;

      &:before,
      &:after {
        position: absolute;
        left: 50%;
        top: 50%;
        transform: translate(-50%, -50%);
      }

      &:before {
        background-color: var(--main-light);
        color: var(--main-dark-opacity-48);
        content: 'or';
        font-size: 16px;
        padding: var(--spacing-12);
        z-index: 2;
      }

      &:after {
        border-top: 1px solid var(--main-dark-opacity-18);
        content: '';
        width: 100%;
      }
    }

    .input-group {
      position: relative;
      padding-bottom: var(--spacing-12);

      &.links {
        display: flex;
        justify-content: space-between;
        font-size: 14px;
      }

      input.invalid-field {
        border-color: var(--main-red);
      }

      .alert {
        color: var(--main-red);
      }

      .icon-eye {
        cursor: pointer;
        position: absolute;
        right: var(--spacing-16);
        top: var(--spacing-14);
      }
    }

    .submit {
      margin: var(--spacing-12) auto var(--spacing-36);
      width: 100%;
      font-weight: 600;
      font-size: 18px;
    }

    .or-sign-up {
      color: var(--main-dark-opacity-48);
      text-align: center;
      margin: 0;
      line-height: 150%;

      span {
        color: var(--link-color);
        cursor: pointer;
      }
    }

    @include bp($bp-mobile) {
      padding: var(--spacing-24);
      max-width: 100%;
    }
  }

  .header {
    &.back-action {
      position: relative;
      padding-left: var(--spacing-8);

      .back-icon {
        position: absolute;
        left: -6px;
        top: 50%;
        padding: var(--spacing-6);
        transform: translateY(-50%);
        cursor: pointer;
      }
    }
  }

  .social-login-buttons {
    display: block;
    overflow: hidden;

    > div {
      border-radius: 5px;
      border: 1px solid var(--main-dark-opacity-12);
      float: left;
      height: 52px;
      width: 52px;

      &:hover {
        background-color: var(--main-light-shade-1);
        border: 1px solid var(--main-dark-opacity-12);
      }

      &:not(:last-of-type) {
        margin-right: 12px;
      }

      &.google-btn {
        width: calc(100% - 128px);
      }
    }
  }

  @include bp($bp-mobile) {
    .form-wrap {
      h1 {
        margin-bottom: var(--spacing-36);
      }

      .submit {
        margin: var(--spacing-18) auto var(--spacing-30);
      }
    }
  }
</style>
